<template>
  <LayupSpinner v-if="isLoading" :size="10" />
  <div v-show="!isLoading">
    <div>
      <h1 class="font-semibold text-xl text-grey-light-900">Notifications</h1>
      <p class="text-grey-light-600 text-sm">
        These notifications are sent out to your customers when certain events occur.
      </p>
    </div>
    <SectionDivider class="my-4" />
    <form @submit.prevent="onSubmit">
      <!-- Accounts -->
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Accounts</h5>
          <p class="text-grey-light-600 text-sm">Manage notifications for settlements and invoices.</p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <NotificationInput
              :section="formMerchant.settlements"
              @update:section="onUpdateSection"
              section-key="settlements"
              label="Settlements"
              sub-text="Receive an email with details of a settlement."
            />

            <NotificationInput
              :section="formMerchant.invoices"
              @update:section="onUpdateSection"
              section-key="invoices"
              label="Invoices"
              sub-text="Receive an email containing your invoice for settlement fees."
            />
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <!-- Orders -->
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Orders</h5>
          <p class="text-grey-light-600 text-sm">
            Manage notifications for order confirmations, cancellations, and refunds.
          </p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <NotificationInput
              :section="formMerchant.newOrder"
              @update:section="onUpdateSection"
              section-key="newOrder"
              label="New Order"
              sub-text="Receive an email notification each time a new order is place and LayUp plan is activated."
            />

            <NotificationInput
              :section="formMerchant.amendmentOrder"
              @update:section="onUpdateSection"
              section-key="amendmentOrder"
              label="Amendment"
              sub-text="Receive an email notification each time an order is amended."
            />

            <NotificationInput
              :section="formMerchant.expiredOrder"
              @update:section="onUpdateSection"
              section-key="expiredOrder"
              label="Expired"
              sub-text="Receive an email notification when no deposit is received before the order expires."
            />

            <NotificationInput
              :section="formMerchant.suspendedOrder"
              @update:section="onUpdateSection"
              section-key="suspendedOrder"
              label="Suspended"
              sub-text="Receive an email notification when order is suspended."
            />

            <NotificationInput
              :section="formMerchant.cancelledOrder"
              @update:section="onUpdateSection"
              section-key="cancelledOrder"
              label="Cancelled"
              sub-text="Receive an email notification when order is cancelled."
            />

            <NotificationInput
              :section="formMerchant.cancellationRequest"
              @update:section="onUpdateSection"
              section-key="cancellationRequest"
              label="Cancellation/Refund Request"
              sub-text="Receive an email notification when customers initiate a cancellation or refund request."
            />

            <NotificationInput
              :section="formMerchant.completedOrder_75"
              @update:section="onUpdateSection"
              section-key="completedOrder_75"
              label="% Complete"
              sub-text="Receive an email notification when selected order percentage is complete."
            />

            <NotificationInput
              :section="formMerchant.completedOrder"
              @update:section="onUpdateSection"
              section-key="completedOrder"
              label="Completed"
              sub-text="Receive an email notification when existing orders are complete."
            />
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <!-- Payments -->
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Payments</h5>
          <p class="text-grey-light-600 text-sm">Allow users to pay instalments on a weekly basis.</p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <NotificationInput
              :section="formMerchant.successfulPayment"
              @update:section="onUpdateSection"
              section-key="successfulPayment"
              label="Successful"
              sub-text="Receive an email notification each time an ad-hoc or recurring payment is made towards an active order."
            />

            <NotificationInput
              :section="formMerchant.failedPayment"
              @update:section="onUpdateSection"
              section-key="failedPayment"
              label="Failed"
              sub-text="Receive an email notification each time recurring payment fails for an existing payment plan."
            />

            <NotificationInput
              :section="formMerchant.missedPayment"
              @update:section="onUpdateSection"
              section-key="missedPayment"
              label="Missed"
              sub-text="Receive an email notification each time a recurring payment is missed."
            />

            <NotificationInput
              :section="formMerchant.refunds"
              @update:section="onUpdateSection"
              section-key="refunds"
              label="Refunds"
              sub-text="Receive an email notification when a refund is successfully processed to a customer."
            />

            <NotificationInput
              :section="formMerchant.transfers"
              @update:section="onUpdateSection"
              section-key="transfers"
              label="Transfers"
              sub-text="Receive an email notification when a transfer is processed between two existing payment plans."
            />
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="flex justify-end gap-4 mt-4 mr-8">
        <Button class="w-auto" type="submit">Save</Button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import type { FormMerchantNotifications, NotificationSetting } from '@/models/settings';
import { useMerchantStore } from '@/stores/merchants';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import type { ModelMerchantResponse } from '@layup/api-sdk';
import NotificationInput from '../NotificationInput.vue';
import type { NotificationSettings } from '../utils';

const props = defineProps<{
  settings: NotificationSettings;
  merchantId: string;
}>();
const isLoading = ref(false);

const formMerchant = ref<FormMerchantNotifications>({
  settlements: {
    enabled: false,
    emails: [],
  },
  invoices: {
    enabled: false,
    emails: [],
  },
  newOrder: {
    enabled: false,
    emails: [],
  },
  amendmentOrder: {
    enabled: false,
    emails: [],
  },
  expiredOrder: {
    enabled: false,
    emails: [],
  },
  suspendedOrder: {
    enabled: false,
    emails: [],
  },
  cancelledOrder: {
    enabled: false,
    emails: [],
  },
  cancellationRequest: {
    enabled: false,
    emails: [],
  },
  completedOrder_75: {
    enabled: false,
    emails: [],
    value: 0,
  },
  completedOrder: {
    enabled: false,
    emails: [],
  },
  successfulPayment: {
    enabled: false,
    emails: [],
  },
  failedPayment: {
    enabled: false,
    emails: [],
  },
  missedPayment: {
    enabled: false,
    emails: [],
  },
  refunds: {
    enabled: false,
    emails: [],
  },
  transfers: {
    enabled: false,
    emails: [],
  },
});

const emit = defineEmits(['save:merchant']);
const toast = useToast();
const merchantStore = useMerchantStore();

watch(
  () => props.settings,
  (ns) => {
    if (!ns) {
      isLoading.value = false;
      return;
    }
    isLoading.value = true;
    const fm = formMerchant.value;

    fm.settlements = { enabled: ns.settlements?.enabled || false, emails: ns.settlements?.emails || [] };
    fm.invoices = { enabled: ns.invoices?.enabled || false, emails: ns.invoices?.emails || [] };
    fm.newOrder = { enabled: ns.newOrder?.enabled || false, emails: ns.newOrder?.emails || [] };
    fm.amendmentOrder = { enabled: ns.amendmentOrder?.enabled || false, emails: ns.amendmentOrder?.emails || [] };
    fm.expiredOrder = { enabled: ns.expiredOrder?.enabled || false, emails: ns.expiredOrder?.emails || [] };
    fm.suspendedOrder = { enabled: ns.suspendedOrder?.enabled || false, emails: ns.suspendedOrder?.emails || [] };
    fm.cancelledOrder = { enabled: ns.cancelledOrder?.enabled || false, emails: ns.cancelledOrder?.emails || [] };
    fm.cancellationRequest = {
      enabled: ns.cancellationRequest?.enabled || false,
      emails: ns.cancellationRequest?.emails || [],
    };
    fm.completedOrder_75 = {
      enabled: ns.completedOrder_75?.enabled || false,
      emails: ns.completedOrder_75?.emails || [],
      value: ns.completedOrder_75?.value || 0,
    };
    fm.completedOrder = { enabled: ns.completedOrder?.enabled || false, emails: ns.completedOrder?.emails || [] };
    fm.successfulPayment = {
      enabled: ns.successfulPayment?.enabled || false,
      emails: ns.successfulPayment?.emails || [],
    };
    fm.failedPayment = { enabled: ns.failedPayment?.enabled || false, emails: ns.failedPayment?.emails || [] };
    fm.missedPayment = { enabled: ns.missedPayment?.enabled || false, emails: ns.missedPayment?.emails || [] };
    fm.refunds = { enabled: ns.refunds?.enabled || false, emails: ns.refunds?.emails || [] };
    fm.transfers = { enabled: ns.transfers?.enabled || false, emails: ns.transfers?.emails || [] };

    isLoading.value = false;
  },
  { immediate: true },
);

const onUpdateSection = ({ section, sectionKey }: { section: NotificationSetting; sectionKey: string }) => {
  if (formMerchant.value[sectionKey]) {
    formMerchant.value[sectionKey] = { ...section };
  }
};

const onSubmit = async () => {
  try {
    isLoading.value = true;
    if (props.merchantId === '') {
      toast.error('Merchant ID is not set. Please try reloading the page', { position: 'top' });
      throw new Error('Merchant ID is required.');
    }

    const settings: ModelMerchantResponse = { ...props.settings, ...formMerchant.value };

    const response = await merchantStore.saveMerchantSettings({ id: props.merchantId, modelMerchantRequest: settings });
    emit('save:merchant', response);

    addMetric('settings-page-notifications', { status: 'success', channel: 'merchant', method: 'saveMerchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-page-notifications', {
        status: 'network_error',
        channel: 'merchant',
        method: 'saveMerchant',
      });
    } else {
      createException(error);
      addMetric('settings-page-notifications', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
    }
  }
};
</script>
